import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import Episodes from "./components/Episodes";
import GuestDirectory from "./components/GuestDirectory";
import UpcomingEpisodes from "./components/UpcomingEpisodes";
import SponsorsPartnerships from "./components/SponsorsPartnerships";
import SubmitGuest from "./components/SubmitGuest";
import DonatePage from "./components/DonatePage";
import PaymentSuccessModal from "./components/PaymentSuccessModal";

// Admin Components
import AdminLogin from "./components/AdminLogin";
import AdminLayout from "./components/AdminLayout";
import AdminDashboard from "./components/AdminDashboard";
import AdminEpisodes from "./components/AdminEpisodes";
import AdminEpisodeForm from "./components/AdminEpisodeForm";
import AdminGuests from "./components/AdminGuests";
import AdminGuestForm from "./components/AdminGuestForm";
import AdminUpcoming from "./components/AdminUpcoming";
import AdminUpcomingForm from "./components/AdminUpcomingForm";
import AdminSponsors from "./components/AdminSponsors";
import AdminSponsorForm from "./components/AdminSponsorForm";
import AdminMedia from "./components/AdminMedia";
import AdminNewsletter from "./components/AdminNewsletter";
import AdminMessages from "./components/AdminMessages";
import AdminDonations from "./components/AdminDonations";

// Check if user is authenticated
const isAuthenticated = () => {
  const token = localStorage.getItem('adminToken');
  return token !== null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <AdminLogin onLogin={() => window.location.reload()} />;
  }
  return children;
};

// Responsive Navigation Component
const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const HamburgerIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );

  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 bg-midnight/80 backdrop-blur-lg border-b border-slate-dark/30 z-40">
      <div className="px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://agents.beunframed.com/wp-content/uploads/2025/07/Heading-4.svg" 
                alt="Unframed Talks" 
                className="h-8 w-auto"
              />
            </div>
            
            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-warm-white hover:text-electric-yellow transition-colors duration-300 font-medium">
                Epizode
              </Link>
              <Link to="/guests" className="text-warm-white hover:text-electric-yellow transition-colors duration-300 font-medium">
                Gosti
              </Link>
              <Link to="/upcoming" className="text-warm-white hover:text-electric-yellow transition-colors duration-300 font-medium">
                Uskoro
              </Link>
              <Link to="/sponsors" className="text-warm-white hover:text-electric-yellow transition-colors duration-300 font-medium">
                Partneri
              </Link>
              <Link to="/submit" className="text-warm-white hover:text-electric-yellow transition-colors duration-300 font-medium">
                Predloži gosta
              </Link>
              <Link 
                to="/donate"
                className="bg-electric-yellow text-midnight px-4 py-2 rounded-lg font-bold hover:bg-electric-yellow/90 transition-all duration-300"
              >
                Doniraj
              </Link>
            </div>

            {/* Mobile Hamburger Menu Button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="text-warm-white hover:text-electric-yellow transition-colors duration-300 focus:outline-none"
              >
                <HamburgerIcon />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col h-screen w-screen" style={{backgroundColor: '#0A0A0A'}}>
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-dark/30" style={{backgroundColor: '#0A0A0A'}}>
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/07/Heading-4.svg" 
              alt="Unframed Talks" 
              className="h-8 w-auto"
            />
            <button
              onClick={closeMobileMenu}
              className="text-warm-white hover:text-electric-yellow transition-colors duration-300"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Mobile Menu Links */}
          <div className="flex-1 flex flex-col justify-center items-center space-y-8 px-6" style={{backgroundColor: '#0A0A0A'}}>
            <Link 
              to="/" 
              onClick={closeMobileMenu}
              className="text-3xl font-medium text-warm-white hover:text-electric-yellow transition-colors duration-300"
            >
              Epizode
            </Link>
            <Link 
              to="/guests" 
              onClick={closeMobileMenu}
              className="text-3xl font-medium text-warm-white hover:text-electric-yellow transition-colors duration-300"
            >
              Gosti
            </Link>
            <Link 
              to="/upcoming" 
              onClick={closeMobileMenu}
              className="text-3xl font-medium text-warm-white hover:text-electric-yellow transition-colors duration-300"
            >
              Uskoro
            </Link>
            <Link 
              to="/sponsors" 
              onClick={closeMobileMenu}
              className="text-3xl font-medium text-warm-white hover:text-electric-yellow transition-colors duration-300"
            >
              Partneri
            </Link>
            <Link 
              to="/submit" 
              onClick={closeMobileMenu}
              className="text-3xl font-medium text-warm-white hover:text-electric-yellow transition-colors duration-300"
            >
              Predloži gosta
            </Link>
            <Link 
              to="/donate" 
              onClick={closeMobileMenu}
              className="bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold text-xl hover:bg-electric-yellow/90 transition-all duration-300 w-full text-center inline-block"
            >
              Doniraj
            </Link>
          </div>
        </div>
      )}

      {/* Payment Success Confirmation Modal */}
      <PaymentSuccessModal />
    </nav>
  );
};

function App() {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(isAuthenticated());

  const handleAdminLogin = (token) => {
    setIsAdminAuthenticated(true);
  };

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <>
              <Navigation />
              <Episodes />
            </>
          } />
          <Route path="/guests" element={
            <>
              <Navigation />
              <GuestDirectory />
            </>
          } />
          <Route path="/upcoming" element={
            <>
              <Navigation />
              <UpcomingEpisodes />
            </>
          } />
          <Route path="/sponsors" element={
            <>
              <Navigation />
              <SponsorsPartnerships />
            </>
          } />
          <Route path="/submit" element={
            <>
              <Navigation />
              <SubmitGuest />
            </>
          } />
          <Route path="/donate" element={
            <>
              <Navigation />
              <DonatePage />
            </>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin onLogin={handleAdminLogin} />} />
          <Route path="/admin/login" element={<AdminLogin onLogin={handleAdminLogin} />} />
          
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/episodes" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminEpisodes />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/episodes/create" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminEpisodeForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/episodes/edit/:id" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminEpisodeForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/guests" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminGuests />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/guests/create" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminGuestForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/guests/edit/:id" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminGuestForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/upcoming" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminUpcoming />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/upcoming/create" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminUpcomingForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/upcoming/edit/:id" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminUpcomingForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/sponsors" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminSponsors />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/sponsors/create" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminSponsorForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/sponsors/edit/:id" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminSponsorForm />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/media" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminMedia />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/newsletter" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminNewsletter />
              </AdminLayout>
            </ProtectedRoute>
          } />

          <Route path="/admin/messages" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminMessages />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/donations" element={
            <ProtectedRoute>
              <AdminLayout onLogout={handleAdminLogout}>
                <AdminDonations />
              </AdminLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;